import { ChangeEvent } from 'react'

import { Helmet } from 'react-helmet-async'

import { faCog } from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Columns, DashboardModule, Form, LoadingModule,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgency from '@hooks/useAgency'
import useAgencySelector from '@hooks/useAgencySelector'
import useSelector from '@hooks/useSelector'
import useServiceProducts from '@hooks/useServiceProducts'
import useServices from '@hooks/useServices'

import EntitySelectBox from '@components/EntitySelectBox'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageHeader from '@components/PageHeader'

import ProductListItem from '../ProductListItem'

const defaultState = {
  selectedServiceId: '',
}

const PageContent = () => {
  const [state, setState] = useSetState(defaultState)
  const { selectedServiceId } = state

  const { selectedAgency } = useAgencySelector()

  const { loading: loadingServices } = useServices({ performHttpRequests: true })

  const { services } = useSelector(reduxState => reduxState.entities)
  const selectedService = services[selectedServiceId] || {}

  const serviceProductsPayload = useServiceProducts(
    { filters: { service_id: selectedServiceId }, performHttpRequests: !!selectedServiceId },
  )
  const {
    filteredServiceProducts,
    hasFilteredServiceProducts,
    loading: loadingServiceProducts,
  } = serviceProductsPayload

  const { callbacks: { updateAgency } } = useAgency(selectedAgency)

  const hiddenProductIds = digObject(selectedAgency, 'options.hidden_product_ids', [])

  return (
    <>
      <Helmet>
        <title>Products | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="products"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={[
          // @ts-expect-error TS2741
          {
            icon: faCog,
            key: 'products',
            title: 'Products',
          },
        ]}
        title="Products"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.manageProducts.banner" />

            <LoadingModule loading={loadingServices} times={3} />

            {!loadingServices && (
              <DashboardModule boxProps={{ marginBottom: 'large' }} title="Select Service">
                <Box flexDirection="column" padding="large">
                  <Form.Field direction="column">
                    <EntitySelectBox
                      entityKey="services"
                      // @ts-expect-error TS2322
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => setState({ selectedServiceId: e.target.value })}
                      value={selectedServiceId}
                    />
                  </Form.Field>
                </Box>
              </DashboardModule>
            )}

            {!!selectedServiceId && (
              <DashboardModule
                boxProps={{ marginBottom: 'large' }}
                loading={loadingServiceProducts}
                title={selectedService.title}
              >
                {!loadingServiceProducts && (
                  <Box flexDirection="column">
                    {filteredServiceProducts.map(filteredServiceProduct => (
                      <ProductListItem
                        hiddenProductIds={hiddenProductIds}
                        key={filteredServiceProduct.id}
                        serviceProduct={filteredServiceProduct}
                        updateAgency={updateAgency}
                      />
                    ))}

                    {!hasFilteredServiceProducts && (
                      <DashboardModule.BlankState>
                        <DashboardModule.BlankState.Title>
                          No Service Products Present.
                        </DashboardModule.BlankState.Title>
                      </DashboardModule.BlankState>
                    )}
                  </Box>
                )}
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.manageProducts.sidebar" />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
